/**
 * DevSwitcher — floating pill that lives on every page while DEV_MODE=true.
 *
 * Kills the tedium of "sign out → sign back in as admin → /admin?tab=dev → click".
 * Instead:
 *   • Small pill bottom-left showing current role + name.
 *   • Click to expand: search + list of every test account.
 *   • First row pinned: "🛡 Back to admin" (jumps straight to /admin).
 *   • Second row pinned: "Sign out" (blows the cookie and lands on /welcome).
 *   • Any other row: one-tap sign in as that account, land on /.
 *
 * Renders NOTHING unless DEV_MODE=true — the /dev/status check returns 404 in
 * prod, so production builds see nothing here. Safe to ship.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Terminal, X, LogIn, Shield, LogOut, Search, ChevronUp } from "lucide-react";

const ROLE_TINT = {
    explorer:     "hsl(220 55% 45%)",
    creator:      "hsl(13 69% 62%)",
    space:        "hsl(131 22% 29%)",
    performer:    "hsl(340 82% 55%)",
    photographer: "hsl(290 45% 55%)",
    seller:       "hsl(27 87% 60%)",
    admin:        "hsl(0 0% 20%)",
    user:         "hsl(30 12% 55%)",
};

export default function DevSwitcher() {
    const { user, refresh, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // null = unknown (still checking), false = disabled (hide forever), true = enabled
    const [enabled, setEnabled] = useState(null);
    const [open, setOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [busyId, setBusyId] = useState(null);
    const panelRef = useRef(null);

    // Probe /dev/status once. In prod this returns { enabled: false } → we hide.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dev/status");
                setEnabled(!!data?.enabled);
            } catch {
                setEnabled(false);
            }
        })();
    }, []);

    // Lazy-load account list the first time the panel opens.
    useEffect(() => {
        if (!open || users.length > 0) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/dev/users");
                setUsers(data || []);
            } catch (e) {
                toast.error(apiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [open, users.length]);

    // Close on outside click / Esc
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (enabled !== true) return null;

    // Never render on public marketing pages — they're for anonymous visitors.
    const hiddenRoutes = ["/welcome", "/pitch"];
    if (hiddenRoutes.some((p) => location.pathname === p)) return null;

    const admin = users.find((u) => u.role === "admin");
    const others = users
        .filter((u) => u.role !== "admin")
        .filter((u) => {
            if (!filter) return true;
            const q = filter.toLowerCase();
            return (u.email || "").toLowerCase().includes(q)
                || (u.name || "").toLowerCase().includes(q)
                || (u.role || "").includes(q);
        });

    const switchTo = async (u, dest = "/") => {
        setBusyId(u.user_id);
        try {
            await api.post("/dev/login-as", { user_id: u.user_id });
            await refresh();
            toast.success(`Now signed in as ${u.name || u.email}`);
            setOpen(false);
            setFilter("");
            navigate(dest);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const backToAdmin = async () => {
        if (!admin) {
            toast.error("No admin account found in DB");
            return;
        }
        await switchTo(admin, "/admin");
    };

    const signOut = async () => {
        try {
            await logout?.();
        } catch {}
        setOpen(false);
        navigate("/welcome");
    };

    const currentRole = user?.role || "guest";
    const currentTint = ROLE_TINT[currentRole] || ROLE_TINT.user;

    return (
        <div
            ref={panelRef}
            data-testid="dev-switcher"
            className="fixed z-[9998]"
            style={{ left: 12, bottom: 12 }}
        >
            {/* Expanded panel */}
            {open && (
                <div
                    data-testid="dev-switcher-panel"
                    className="absolute bottom-12 left-0 w-[320px] max-h-[68vh] bg-white/95 backdrop-blur-lg
                               rounded-2xl border border-stone-200 shadow-2xl overflow-hidden flex flex-col
                               animate-in fade-in slide-in-from-bottom-2 duration-150"
                >
                    <div className="flex items-center gap-2 px-3 py-2.5 border-b border-stone-200 bg-amber-50/60">
                        <Terminal size={13} className="text-amber-700" />
                        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-800 flex-1">
                            Dev switcher
                        </div>
                        <button onClick={() => setOpen(false)} data-testid="dev-switcher-close"
                                className="text-stone-500 hover:text-stone-800 -mr-1 p-1">
                            <X size={14} />
                        </button>
                    </div>

                    {/* Pinned actions */}
                    <div className="p-2 flex flex-col gap-1 border-b border-stone-200">
                        <button
                            data-testid="dev-switcher-back-to-admin"
                            onClick={backToAdmin}
                            disabled={!admin || busyId === admin?.user_id}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-stone-900 text-white
                                       hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed
                                       text-sm font-medium text-left w-full transition-colors"
                        >
                            <Shield size={14} />
                            <span className="flex-1">Back to admin</span>
                            {busyId === admin?.user_id && <span className="text-xs opacity-70">…</span>}
                        </button>
                        <button
                            data-testid="dev-switcher-signout"
                            onClick={signOut}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white
                                       hover:bg-stone-50 border border-stone-200
                                       text-sm text-stone-700 text-left w-full transition-colors"
                        >
                            <LogOut size={14} />
                            <span className="flex-1">Sign out</span>
                        </button>
                    </div>

                    {/* Search */}
                    <div className="px-2 pt-2 pb-1">
                        <div className="relative">
                            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                                data-testid="dev-switcher-filter"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter accounts…"
                                autoFocus
                                className="w-full pl-7 pr-2.5 py-1.5 text-xs rounded-lg border border-stone-200
                                           focus:outline-none focus:border-stone-400 bg-white"
                            />
                        </div>
                    </div>

                    {/* Account list */}
                    <div className="flex-1 overflow-y-auto px-2 pb-2">
                        {loading && (
                            <div className="text-center text-xs text-muted-foreground py-6">Loading accounts…</div>
                        )}
                        {!loading && others.length === 0 && (
                            <div className="text-center text-xs text-muted-foreground py-6">
                                {filter ? `No matches for "${filter}"` : "No test accounts yet"}
                            </div>
                        )}
                        <div className="flex flex-col gap-0.5">
                            {others.map((u) => {
                                const tint = ROLE_TINT[u.role] || ROLE_TINT.user;
                                const isMe = u.user_id === user?.user_id;
                                return (
                                    <button
                                        key={u.user_id}
                                        data-testid={`dev-switcher-user-${u.user_id}`}
                                        disabled={busyId === u.user_id || isMe}
                                        onClick={() => switchTo(u)}
                                        className={`group flex items-center gap-2.5 px-2 py-1.5 rounded-lg
                                                   text-left transition-colors w-full
                                                   ${isMe
                                                       ? "bg-emerald-50 border border-emerald-200 cursor-default"
                                                       : "hover:bg-stone-50 border border-transparent"}`}
                                    >
                                        <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold uppercase"
                                             style={{ background: `${tint}22`, color: tint }}>
                                            {(u.role || "?")[0]}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs font-medium text-stone-800 truncate">
                                                {u.name || u.email}
                                                {isMe && <span className="ml-1.5 text-[9px] font-bold text-emerald-700 uppercase">current</span>}
                                            </div>
                                            <div className="text-[10px] text-stone-500 truncate">
                                                {u.role} · {u.email}
                                            </div>
                                        </div>
                                        {!isMe && (
                                            <LogIn size={11} className="text-stone-400 opacity-100 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 transition-opacity" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Collapsed pill */}
            <button
                data-testid="dev-switcher-toggle"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full
                           bg-white/95 backdrop-blur-lg border border-stone-200 shadow-lg
                           hover:shadow-xl hover:border-stone-300 transition-all
                           text-xs font-medium text-stone-800"
                title="Dev switcher — jump between test accounts"
            >
                <span className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: `${currentTint}22`, color: currentTint }}>
                    <Terminal size={11} />
                </span>
                <span className="max-w-[160px] truncate">
                    {user ? (user.name || user.email) : "Signed out"}
                </span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full uppercase font-bold"
                      style={{ background: `${currentTint}22`, color: currentTint }}>
                    {currentRole}
                </span>
                <ChevronUp size={12} className={`text-stone-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
        </div>
    );
}
